/** Fired after canvas snapshot restore attempt completes for a site. */
export const CANVAS_RESTORE_RESOLVED_EVENT = 'landi:canvas-restore-resolved';

export interface CanvasRestoreResolvedDetail {
  siteId: string;
  /** True when a non-empty snapshot was loaded onto the editor. */
  hadSnapshot: boolean;
}

const resolvedBySite = new Map<string, boolean>();

export function markCanvasRestoreResolved(siteId: string, hadSnapshot: boolean): void {
  resolvedBySite.set(siteId, hadSnapshot);
  window.dispatchEvent(
    new CustomEvent<CanvasRestoreResolvedDetail>(CANVAS_RESTORE_RESOLVED_EVENT, {
      detail: { siteId, hadSnapshot },
      bubbles: true,
      composed: true,
    }),
  );
}

export function getCanvasRestoreResolved(siteId: string): boolean | undefined {
  return resolvedBySite.get(siteId);
}

export function clearCanvasRestoreResolved(siteId: string): void {
  resolvedBySite.delete(siteId);
}

/** Wait until snapshot restore resolves (or times out — treat as no snapshot). */
export function waitForCanvasRestoreResolved(
  siteId: string,
  timeoutMs: number = 10_000,
): Promise<boolean> {
  const existing = resolvedBySite.get(siteId);
  if (existing !== undefined) {
    return Promise.resolve(existing);
  }

  return new Promise<boolean>((resolve) => {
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<CanvasRestoreResolvedDetail>).detail;
      if (!detail || detail.siteId !== siteId) return;
      cleanup();
      resolve(detail.hadSnapshot);
    };

    const timer = window.setTimeout(() => {
      cleanup();
      resolve(false);
    }, timeoutMs);

    const cleanup = (): void => {
      window.removeEventListener(CANVAS_RESTORE_RESOLVED_EVENT, handler);
      window.clearTimeout(timer);
    };

    window.addEventListener(CANVAS_RESTORE_RESOLVED_EVENT, handler);
  });
}
